import { REFERRER, TOUCH_COOKIE, TOUCH_FIELDS } from "~/protocol/source";

// The tab's first touch (protocol/source.ts): the first page this tab opened on
// our origin, its `utm_*` parameters and the page it came from. Kept in sessionStorage, so it
// outlasts the sign-up portal's round trip (same tab) and a mail link opened in a new tab starts
// its own. Every page then copies it into a session cookie, which is how it reaches the server:
// the request that makes the users row carries it, whichever request that is. First-party only,
// and independent of the cookie banner and PostHog. The server filters our own hosts out of the
// referrer and bounds what it stores (server/source.ts); here each value is only clipped so the
// cookie stays under the browser's 4 KB.

const KEY = "featurette-first-touch";
/** Per value, encoded: six of them stay under 4 KB with room for the keys. */
const MAX_ENCODED = 500;

const encodedLength = (value: string) => new URLSearchParams({ v: value }).toString().length - 2;

/** Encoding goes code point by code point, so a prefix's encoded length is the sum of its
 * characters'. */
function clip(value: string): string {
  if (encodedLength(value) <= MAX_ENCODED) return value;
  let out = "";
  let length = 0;
  for (const ch of value) {
    length += encodedLength(ch);
    if (length > MAX_ENCODED) break;
    out += ch;
  }
  return out;
}

/** This page as a touch: its parameters and referrer, URL-encoded. */
function thisPage(): string {
  const params = new URLSearchParams(location.search);
  const touch = new URLSearchParams();
  for (const [param] of TOUCH_FIELDS) {
    const value = (param === REFERRER ? document.referrer : params.get(param))?.trim();
    if (value) touch.set(param, clip(value));
  }
  return touch.toString();
}

function hasCookie(): boolean {
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${TOUCH_COOKIE}=`));
}

/** This tab's touch: the one kept in session storage, recorded from this page if there is none.
 * Without session storage the cookie alone holds it, for the browser session; null = keep it. */
function tabTouch(): string | null {
  try {
    const kept = sessionStorage.getItem(KEY);
    if (kept !== null) return kept;
    const touch = thisPage();
    sessionStorage.setItem(KEY, touch);
    return touch;
  } catch {
    return hasCookie() ? null : thisPage();
  }
}

/** Records this tab's first touch on its first page, and hands it to the server in the cookie.
 * The touch is recorded before this returns; the promise settles once the cookie is written.
 * Called on every page load, and awaited before a request that may make the users row (the guest
 * form's POST, the gate turning ready) or the redirect to the portal, since another tab may have
 * written the cookie since. Never rejects: a page that may not touch cookies or storage records
 * nothing and carries on. */
export async function keepFirstTouch(): Promise<void> {
  try {
    const touch = tabTouch();
    if (touch === null) return;
    // Absent outside a secure context, and in browsers older than Firefox 140 or Safari 18.4.
    const store: CookieStore | undefined = window.cookieStore;
    if (store) {
      await store.set({ name: TOUCH_COOKIE, value: touch, path: "/", sameSite: "lax" });
      return;
    }
    const secure = location.protocol === "https:" ? "; Secure" : "";
    // biome-ignore lint/suspicious/noDocumentCookie: the fallback where cookieStore is absent
    document.cookie = `${TOUCH_COOKIE}=${touch}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // An opaque origin (a sandboxed frame) throws on document.cookie and rejects cookieStore.set.
  }
}
